import axios, { AxiosResponse } from "axios";
import type { iOrganization, iUser } from "@sdk/user-management/user-management.models";
import type { PaginatedOptions, PaginatedResults } from "@sdk/utils/paginated-results";
import type {
  ContactBrandMigrationReport,
  ContactOwnerMigrationReport,
  EnquiryAttributesMigrationReport,
  EnquiryBrandMigrationReport,
  HqMigrationRunOptions,
  HqOrganizationDeletionPreview,
  BrandDefaultLedgersMigrationReport,
  BrandHomeCurrencyMigrationReport,
  BrandIdentityConsolidationReport,
  QuoteConfirmationFlowMigrationReport,
  QuoteCtaBlockMigrationReport,
  UserAccessMigrationReport,
} from "./hq.models";

export class HqService {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }

  constructor(protected config: { basePath: string; token: string }) {}

  async login(email: string, password: string) {
    const results: AxiosResponse<{ accessToken: string }> = await axios.post(
      `${this.config.basePath}/hq/login`,
      { email, password }
    );
    return results.data;
  }

  async queryOrganizations(body: {
    query?: Record<string, unknown>;
    options?: PaginatedOptions;
  }): Promise<PaginatedResults<iOrganization>> {
    const results: AxiosResponse<PaginatedResults<iOrganization>> = await axios.post(
      `${this.config.basePath}/hq/organizations/query`,
      body,
      {
        headers: {
          Authorization: this.config.token,
        },
      }
    );
    return results.data;
  }

  async queryUsers(body: {
    query?: Record<string, unknown>;
    options?: PaginatedOptions;
  }): Promise<PaginatedResults<iUser>> {
    const results: AxiosResponse<PaginatedResults<iUser>> = await axios.post(
      `${this.config.basePath}/hq/users/query`,
      body,
      {
        headers: {
          Authorization: this.config.token,
        },
      }
    );
    return results.data;
  }

  async generateAccessToken(userId: string) {
    const results: AxiosResponse<{ status: boolean; accessToken: string }> =
      await axios.post(
        `${this.config.basePath}/hq/generate-access-token`,
        { userId },
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async getOrganizationDeletionPreview(organizationId: string) {
    const results: AxiosResponse<HqOrganizationDeletionPreview> = await axios.post(
      `${this.config.basePath}/hq/organizations/${organizationId}/deletion-preview`,
      {},
      {
        headers: {
          Authorization: this.config.token,
        },
      }
    );
    return results.data;
  }

  async deleteOrganization(organizationId: string, confirmation: string) {
    const results: AxiosResponse<{ status: boolean }> = await axios.post(
      `${this.config.basePath}/hq/organizations/${organizationId}/delete`,
      { confirmation },
      {
        headers: {
          Authorization: this.config.token,
        },
      }
    );
    return results.data;
  }

  async migrateUserAccess(
    body?: HqMigrationRunOptions
  ): Promise<UserAccessMigrationReport[]> {
    const results: AxiosResponse<UserAccessMigrationReport[]> = await axios.post(
      `${this.config.basePath}/hq/migrations/user-access`,
      body ?? {},
      {
        headers: {
          Authorization: this.config.token,
        },
      }
    );
    return results.data;
  }

  async migrateEnquiryBrands(
    body?: HqMigrationRunOptions
  ): Promise<EnquiryBrandMigrationReport[]> {
    const results: AxiosResponse<EnquiryBrandMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/enquiry-brands`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateEnquiryAttributes(
    body?: HqMigrationRunOptions
  ): Promise<EnquiryAttributesMigrationReport[]> {
    const results: AxiosResponse<EnquiryAttributesMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/enquiry-attributes`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateContactOwners(
    body?: HqMigrationRunOptions
  ): Promise<ContactOwnerMigrationReport[]> {
    const results: AxiosResponse<ContactOwnerMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/contact-owners`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateContactBrands(
    body?: HqMigrationRunOptions
  ): Promise<ContactBrandMigrationReport[]> {
    const results: AxiosResponse<ContactBrandMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/contact-brands`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateBrandIdentityConsolidation(
    body?: HqMigrationRunOptions
  ): Promise<BrandIdentityConsolidationReport[]> {
    const results: AxiosResponse<BrandIdentityConsolidationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/brand-identity-consolidation`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateBrandDefaultLedgers(
    body?: HqMigrationRunOptions
  ): Promise<BrandDefaultLedgersMigrationReport[]> {
    const results: AxiosResponse<BrandDefaultLedgersMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/brand-default-ledgers`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateBrandHomeCurrency(
    body?: HqMigrationRunOptions
  ): Promise<BrandHomeCurrencyMigrationReport[]> {
    const results: AxiosResponse<BrandHomeCurrencyMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/brand-home-currency`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateQuoteConfirmationFlow(
    body?: HqMigrationRunOptions
  ): Promise<QuoteConfirmationFlowMigrationReport[]> {
    const results: AxiosResponse<QuoteConfirmationFlowMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/quote-confirmation-flow`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }

  async migrateQuoteCtaBlocks(
    body?: HqMigrationRunOptions
  ): Promise<QuoteCtaBlockMigrationReport[]> {
    const results: AxiosResponse<QuoteCtaBlockMigrationReport[]> =
      await axios.post(
        `${this.config.basePath}/hq/migrations/quote-cta-blocks`,
        body ?? {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
    return results.data;
  }
}
